import { useState } from 'react';
import { Share2 } from 'lucide-react';
import { Button, ButtonProps } from '@/components/ui/button';
import { ShareDialog } from './ShareDialog';
import { ShareContext, defaultShareText, isNativeShareSupported } from '@/lib/share';

interface ShareButtonProps extends Omit<ButtonProps, 'onClick' | 'title'> {
  title: string;
  text?: string;
  url?: string;
  context?: ShareContext;
  label?: string;
  iconOnly?: boolean;
}

export function ShareButton({
  title,
  text,
  url,
  context = 'generic',
  label = 'Del',
  iconOnly = false,
  variant = 'outline',
  size,
  className,
  ...rest
}: ShareButtonProps) {
  const [open, setOpen] = useState(false);

  const handleClick = async () => {
    const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
    const shareText = text || defaultShareText(context, title);

    if (isNativeShareSupported()) {
      try {
        await navigator.share({ title, text: shareText, url: shareUrl });
        return;
      } catch (err: any) {
        // User cancelled — gør intet. Ved andre fejl: fallback til dialog.
        if (err?.name === 'AbortError') return;
      }
    }
    setOpen(true);
  };

  const resolvedUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={iconOnly ? (size ?? 'icon') : size}
        onClick={handleClick}
        className={className}
        aria-label="Del denne side"
        {...rest}
      >
        <Share2 className="w-4 h-4" />
        {!iconOnly && <span>{label}</span>}
      </Button>
      <ShareDialog
        open={open}
        onOpenChange={setOpen}
        title={title}
        text={text}
        url={resolvedUrl}
        context={context}
      />
    </>
  );
}
